import { useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  GripVertical,
  Plus,
  X,
  User,
  DollarSign,
  Globe,
  Tag,
  Calendar,
  Mail,
  Phone,
  Briefcase,
  Building2,
  FileText,
  Heart,
  Percent,
  Layers,
  Users,
  Bug,
} from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { fromJS } from "immutable";
import TaskAction from "../../../actions/TaskAction";

// Field icon mapping
const FIELD_ICONS: Record<string, any> = {
  name: FileText,
  industry: Building2,
  country: Globe,
  labels: Tag,
  openedAt: Calendar,
  birthDate: Calendar,
  email: Mail,
  phone: Phone,
  issue: Bug,
  contact: Users,
  account: Building2,
  description: FileText,
  salutation: Heart,
  jobTitle: Briefcase,
  website: Globe,
  estimatedRevenue: DollarSign,
  estimatedProbability: Percent,
  stage: Layers,
  assignees: User,
};

// Available card fields with their display names
// builtInFor: array of content types where this field is rendered as a built-in card element
// (not shown in the configurable "Visible attributes" list)
const CARD_FIELD_OPTIONS = [
  { id: "name", label: "Name", alwaysVisible: true },
  { id: "industry", label: "Industry" },
  { id: "country", label: "Country" },
  { id: "labels", label: "Labels", builtInFor: ["SALE"] },
  { id: "openedAt", label: "Created Date" },
  { id: "birthDate", label: "Birth Date" },
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone" },
  { id: "issue", label: "Issue" },
  { id: "contact", label: "Contact" },
  { id: "account", label: "Account" },
  { id: "description", label: "Description" },
  { id: "salutation", label: "Salutation" },
  { id: "jobTitle", label: "Job Title" },
  { id: "website", label: "Website" },
  { id: "estimatedRevenue", label: "Deal Value", builtInFor: ["SALE"] },
  { id: "estimatedProbability", label: "Probability", builtInFor: ["SALE"] },
  { id: "stage", label: "Stage" },
];

export const DEFAULT_FIELDS_BY_TYPE = {
  CONTACT: ["name", "email", "phone", "labels", "jobTitle", "country"],
  COMPANY: ["name", "industry", "country", "labels", "openedAt"],
  SALE: [
    "name",
    "labels",
    "estimatedRevenue",
    "estimatedProbability",
    "issue",
    "contact",
    "account",
  ],
};

interface CardRowSettingsProps {
  contentType: string;
}

/** Visible attributes section shown inline in View Settings */
const CardRowSettings = ({ contentType }: CardRowSettingsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";
  const boardCardFields =
    useSelector((state: any) => state.taskReducer.get("boardCardFields")) || fromJS([]);

  const visibleFields = useMemo(() => {
    const fields =
      boardCardFields.size > 0 ? boardCardFields.toJS() : DEFAULT_FIELDS_BY_TYPE[contentType];
    return fields.filter((field) => (isJira ? true : field !== "issue"));
  }, [boardCardFields, contentType, isJira]);

  // Filter field options based on content type
  const getFieldOptions = () => {
    const commonFields = ["name", "labels", "country", "openedAt"];

    switch (contentType) {
      case "COMPANY":
        return CARD_FIELD_OPTIONS.filter((f) =>
          [...commonFields, "industry", "description", "email", "phone", "website"].includes(f.id)
        );
      case "CONTACT":
        return CARD_FIELD_OPTIONS.filter((f) =>
          [
            ...commonFields,
            "account",
            "email",
            "phone",
            "jobTitle",
            "salutation",
            "birthDate",
          ].includes(f.id)
        );
      case "SALE":
        return CARD_FIELD_OPTIONS.filter(
          (f) =>
            [
              "name",
              "labels",
              "estimatedRevenue",
              "estimatedProbability",
              "issue",
              "contact",
              "account",
            ].includes(f.id) && (isJira ? true : f.id !== "issue")
        );
      default:
        return CARD_FIELD_OPTIONS.filter((f) => commonFields.includes(f.id));
    }
  };

  const removeField = (fieldId: string) => {
    const field = CARD_FIELD_OPTIONS.find((f) => f.id === fieldId);
    if (field?.alwaysVisible) return;
    const newFields = visibleFields.filter((f: string) => f !== fieldId);
    dispatch(TaskAction.changeBoardCardFields(newFields));
  };

  const addField = (fieldId: string) => {
    if (!visibleFields.includes(fieldId)) {
      const newFields = [...visibleFields, fieldId];
      dispatch(TaskAction.changeBoardCardFields(newFields));
    }
  };

  // ─── Computed field lists (must be above drag handlers) ───
  const fieldOptions = getFieldOptions();
  // Filter out built-in fields for this content type
  const isBuiltIn = (f: any) => f.builtInFor?.includes(contentType);
  const visibleFieldOptions = fieldOptions.filter(
    (f) => visibleFields.includes(f.id) && !f.alwaysVisible && !isBuiltIn(f)
  );
  // Sort visibleFieldOptions by their position in visibleFields
  const orderedVisibleOptions = [...visibleFieldOptions].sort(
    (a, b) => visibleFields.indexOf(a.id) - visibleFields.indexOf(b.id)
  );
  const availableFields = fieldOptions.filter(
    (f) => !visibleFields.includes(f.id) && !f.alwaysVisible && !isBuiltIn(f)
  );

  // ─── Drag-and-drop state ───
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragCounter = useRef(0);

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Store the index as text so the browser allows the drag
    e.dataTransfer.setData("text/plain", String(index));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverIndex(index);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current++;
  }, []);

  const handleDragLeave = useCallback(() => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setOverIndex(null);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      dragCounter.current = 0;
      if (dragIndex === null || dragIndex === dropIndex) {
        setDragIndex(null);
        setOverIndex(null);
        return;
      }
      // Map UI indices back to visibleFields indices via field IDs
      const dragFieldId = orderedVisibleOptions[dragIndex]?.id;
      const dropFieldId = orderedVisibleOptions[dropIndex]?.id;
      if (!dragFieldId || !dropFieldId) return;

      const realDragIndex = visibleFields.indexOf(dragFieldId);
      const realDropIndex = visibleFields.indexOf(dropFieldId);
      if (realDragIndex === -1 || realDropIndex === -1) return;

      const newFields = [...visibleFields];
      const [moved] = newFields.splice(realDragIndex, 1);
      newFields.splice(realDropIndex, 0, moved);
      dispatch(TaskAction.changeBoardCardFields(newFields));
      setDragIndex(null);
      setOverIndex(null);
    },
    [dragIndex, visibleFields, orderedVisibleOptions, dispatch]
  );

  const handleDragEnd = useCallback(() => {
    dragCounter.current = 0;
    setDragIndex(null);
    setOverIndex(null);
  }, []);

  return (
    <div className="space-y-1">
      {/* Section header */}
      <div className="text-xs font-medium text-gray-400 px-2 pt-2">{t("Visible attributes")}</div>

      {/* Visible attribute rows */}
      {orderedVisibleOptions.map((field, index) => {
        const IconComponent = FIELD_ICONS[field.id] || FileText;
        const isDragging = dragIndex === index;
        const isOver = overIndex === index && dragIndex !== index;
        return (
          <div
            key={field.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-1.5 px-1 py-1 rounded hover:bg-gray-50 group transition-all ${
              isDragging ? "opacity-40" : ""
            } ${isOver ? "border-t-2 border-blue-400" : "border-t-2 border-transparent"}`}
          >
            {/* Drag handle */}
            <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0 cursor-grab active:cursor-grabbing" />

            {/* Icon */}
            <IconComponent className="w-4 h-4 text-gray-400 shrink-0" />

            {/* Label */}
            <span className="text-sm font-medium text-gray-700 flex-1 truncate">
              {t(field.label)}
            </span>

            {/* Remove button (hidden for alwaysVisible fields) */}
            {!field.alwaysVisible && (
              <button
                onClick={() => removeField(field.id)}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200 transition-opacity"
              >
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
            )}
          </div>
        );
      })}

      {/* Add card row button */}
      {availableFields.length > 0 && (
        <Popover className="relative">
          <PopoverButton className="w-full flex items-center gap-1.5 px-1 py-1 text-sm font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded cursor-pointer outline-none">
            <Plus className="w-4 h-4 ml-3.5" />
            <span>{t("Add card row")}</span>
          </PopoverButton>
          <PopoverPanel anchor="right start" className="[--anchor-gap:12px] z-20">
            <div className="w-48 max-h-60 overflow-y-auto p-1 rounded-md bg-white border border-gray-200 shadow-lg">
              {availableFields.map((field) => {
                const IconComponent = FIELD_ICONS[field.id] || FileText;
                return (
                  <PopoverButton
                    key={field.id}
                    as="button"
                    onClick={() => addField(field.id)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                  >
                    <IconComponent className="w-4 h-4 text-gray-400" />
                    {t(field.label)}
                  </PopoverButton>
                );
              })}
            </div>
          </PopoverPanel>
        </Popover>
      )}
    </div>
  );
};

export default CardRowSettings;
